"use client"

import type { TrackEventProperties } from "@/modules/tracking/types"
import type { ElementType, FunctionComponent, PropsWithChildren } from "react"

import { useEffect, useRef, useState } from "react"

import { useTracking } from "@/modules/tracking/useTracking"
import { useIntersectionObserver } from "@/modules/window/hooks/useIntersectionObserver"

type TrackObserverProps = PropsWithChildren<{
  observerOptions?: IntersectionObserverInit
  tagName?: ElementType
  trackingProps: TrackEventProperties
}>

export const TrackObserver: FunctionComponent<TrackObserverProps> = ({
  trackingProps: { eventName, eventProperties },
  observerOptions,
  children,
  tagName: TagName = "div",
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const tracking = useTracking()
  const [refIsLoaded, setRefIsLoaded] = useState(false)
  const [isAlreadyTracked, setIsAlreadyTracked] = useState(false)

  const isObserved = useIntersectionObserver(ref, observerOptions)

  useEffect(() => {
    setRefIsLoaded(ref.current !== null)

    if (refIsLoaded && isObserved && !isAlreadyTracked) {
      tracking?.track(eventName, eventProperties)
      setIsAlreadyTracked(Boolean(tracking))
    }
  }, [
    isAlreadyTracked,
    setIsAlreadyTracked,
    isObserved,
    tracking,
    eventName,
    eventProperties,
    ref,
    refIsLoaded,
    setRefIsLoaded,
  ])

  return <TagName ref={ref}>{children}</TagName>
}
